import { Delete, Error as ErrorIcon } from '@mui/icons-material';
import {
    Box,
    Checkbox,
    IconButton,
    MenuItem,
    Select,
    SelectChangeEvent,
    TableCell,
    Tooltip,
    styled,
} from '@mui/material';
import { memo } from 'react';
import { DesignConceptPreview } from './DesignConceptPreview';

interface RankingRowProps {
    rank: number;
    conceptId: string;
    filteringOptionGroup: string | null;
    defaultOptionGroup: string | null;
    filteringOptionGroupOptions: [string, Models.IngestConfigApi.ForcedRankings.PartialProductSpec][];
    defaultOptionGroupOptions: [string, Models.IngestConfigApi.ForcedRankings.PartialProductSpec][];
    selected: boolean;
    onSelect: (selected: boolean) => void;
    onFilteringOptionChange: (value: string | null) => void;
    onDefaultOptionChange: (value: string | null) => void;
    onDelete: () => void;
    productKey: string;
    cultures: string[];
    duplicateRanks?: number[];
}

const StyledSelect = styled(Select<string>)(() => ({
    width: '100%',
    '& .MuiSelect-select': {
        padding: '8px 14px',
    },
}));

const RankingRowComponent = ({
    rank,
    conceptId,
    filteringOptionGroup,
    defaultOptionGroup,
    filteringOptionGroupOptions,
    defaultOptionGroupOptions,
    selected,
    onSelect,
    onFilteringOptionChange,
    onDefaultOptionChange,
    onDelete,
    productKey,
    cultures,
    duplicateRanks,
}: RankingRowProps): JSX.Element => {
    const handleFilteringOptionChange = (event: SelectChangeEvent<string>): void => {
        const { value } = event.target;

        onFilteringOptionChange(value);
    };

    const handleDefaultOptionChange = (event: SelectChangeEvent<string>): void => {
        const { value } = event.target;

        onDefaultOptionChange(value === '' ? null : value);
    };

    const previewFilteringOptions = filteringOptionGroup
        ? filteringOptionGroupOptions.find(([id, _]) => id === filteringOptionGroup)?.[1].values
        : null;

    const previewDefaultOptions = defaultOptionGroup
        ? defaultOptionGroupOptions.find(([id, _]) => id === defaultOptionGroup)?.[1].values
        : null;

    return (
        <>
            <TableCell sx={{ padding: 4 }}>
                <Checkbox
                    checked={selected}
                    onChange={(e): void => onSelect(e.target.checked)}
                />
            </TableCell>
            <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {rank}
                    {duplicateRanks && (
                        <Tooltip title={`Duplicate of rank${duplicateRanks.length > 1 ? 's' : ''}: ${duplicateRanks.join(', ')}`}>
                            <ErrorIcon color="error" fontSize="small" />
                        </Tooltip>
                    )}
                </Box>
            </TableCell>
            <TableCell sx={{ padding: 2 }}>
                <DesignConceptPreview
                    conceptId={conceptId}
                    cultures={cultures}
                    defaultOptions={previewDefaultOptions ?? {}}
                    filteringOptions={previewFilteringOptions ?? {}}
                    productKey={productKey}
                />
            </TableCell>
            <TableCell>{conceptId}</TableCell>
            <TableCell>
                <StyledSelect
                    displayEmpty
                    value={filteringOptionGroup ?? ''}
                    onChange={handleFilteringOptionChange}
                >
                    <MenuItem value="">
                        <em>None</em>
                    </MenuItem>
                    {filteringOptionGroupOptions.map(([id, spec]) => (
                        <MenuItem key={id} value={id}>
                            {spec.name}
                        </MenuItem>
                    ))}
                </StyledSelect>
            </TableCell>
            <TableCell>
                <StyledSelect
                    displayEmpty
                    value={defaultOptionGroup ?? ''}
                    onChange={handleDefaultOptionChange}
                >
                    <MenuItem value="">
                        <em>None</em>
                    </MenuItem>
                    {defaultOptionGroupOptions.map(([id, spec]) => (
                        <MenuItem key={id} value={id}>
                            {spec.name}
                        </MenuItem>
                    ))}
                </StyledSelect>
            </TableCell>
            <TableCell align="right">
                <IconButton
                    size="small"
                    onClick={onDelete}
                >
                    <Delete />
                </IconButton>
            </TableCell>
        </>
    );
};

export const RankingRow = memo(RankingRowComponent);
